// The Money HTTP client (packages/shared/src/http/money.ts; Slice 6; D27; ADR-0023).
// Every response parses through the SAME shared Zod schema apps/api validates against
// (app-engineering §2 "parse, don't validate" - the Slice-2 wire-drift lesson), never a
// re-derived client-side shape for a LANDED contract. Matches src/api/certify.ts's own
// established convention exactly: inline the literal path string (mirroring
// MONEY_ROUTES's template, http/money.ts:13-16 - no route-builder helper exists in
// @nightcrew/shared for any api/*.ts client in this repo, verified), never hand-roll a
// wire shape a shared schema already defines.
//
// LANDED routes (MONEY_ROUTES, http/money.ts:13-16): `moneyView` (GET, the D27 read
// surface - plan capacity, all-in cost, the abstain-capable P&L) and `billingInbox`
// (POST, one founder-supplied/imported cost item).
//
// THE THIRD ROUTE HAS NOW LANDED and this file's PROPOSED section is gone with it. It
// carried `changeCeiling`, an illustrative shape built against the UX spec's proposed
// `PATCH /v1/apps/:appId/ceiling`. The shipped route differs in three ways that all
// mattered, which is exactly why a proposed client is flagged rather than trusted:
//
//   path  `/ceiling`                -> `/monthly-limit`  (the sanctioned word is LIMIT;
//                                      `ceiling` is banned customer vocabulary)
//   field `monthlyCeilingEurMinor`  -> `monthlyLimitMinor`
//   unit  EUR minor, converted here -> the PLAN'S currency, NOT converted (ADR-0039)
//
// The unit is the one that would have cost money. The proposed client converted the
// founder's USD input to EUR before sending it, on the belief that the column held EUR
// cents. It does not, so a founder typing $500 would have stored 42,750 and had their
// work pause at $427.50 - a guardrail 1.1696x tighter than the one they set.
import { z } from "zod";
import { BillingInboxItem, MonthlyLimitResponse, MoneyView } from "@nightcrew/shared";
import type { AppId, Result } from "@nightcrew/shared";
import { apiFetch, apiPost } from "./client.js";
import type { ClientError } from "./errors.js";

// ============================================================================
// LANDED (http/money.ts:13-16, 22-44)
// ============================================================================

/** GET /v1/apps/:appId/money-view (http/money.ts:14,25-26). */
export const getMoneyView = (appId: AppId): Promise<Result<MoneyView, ClientError>> =>
  apiFetch(`/v1/apps/${appId}/money-view`, MoneyView);

/**
 * POST body: one raw billing-inbox item (http/money.ts:35-40). Omits the
 * server-assigned `id`/`accountId`/`createdAt` exactly as `BillingInboxRequestBody`
 * does - this client never re-derives that shape, it reuses the identical `.omit`
 * projection of the landed `BillingInboxItem` schema.
 */
export const BillingInboxRequestBody = BillingInboxItem.omit({
  id: true,
  accountId: true,
  createdAt: true,
});
export type BillingInboxRequestBody = z.infer<typeof BillingInboxRequestBody>;

/** POST /v1/apps/:appId/billing-inbox (http/money.ts:15,42-44) -> the recorded item. */
export const addBillingInboxItem = (
  appId: AppId,
  body: BillingInboxRequestBody,
): Promise<Result<BillingInboxItem, ClientError>> =>
  apiPost(`/v1/apps/${appId}/billing-inbox`, BillingInboxItem, body);

// ============================================================================
// LANDED (AC3 clause 2, D48): the one-tap monthly-limit adjust
// ============================================================================

/**
 * PATCH /v1/apps/:appId/monthly-limit body, reusing the shared contract rather than a
 * re-derived shape - the same rule this file's header states for every other route.
 *
 * NO CURRENCY CONVERSION ANYWHERE ON THIS PATH. `apps.monthly_ceiling_minor` holds minor
 * units of the account's plan currency (ADR-0039), which is USD for every shipped plan,
 * and the founder types dollars. The two are already the same unit, so the honest
 * transformation is none. `format.ts`'s `usdMinorToEurMinor` exists for the display
 * conversion this path does NOT need, and calling it here is the defect ADR-0039 records.
 */
export { MonthlyLimitRequestBody, MonthlyLimitResponse } from "@nightcrew/shared";

/** PATCH /v1/apps/:appId/monthly-limit -> the stored limit and its source. */
/** POST /v1/apps/:appId/monthly-limit/keep - answer the keep-or-change prompt with "keep". */
export const keepMonthlyLimit = (appId: AppId): Promise<Result<MonthlyLimitResponse, ClientError>> =>
  apiFetch(`/v1/apps/${appId}/monthly-limit/keep`, MonthlyLimitResponse, { method: "POST" });

export const setMonthlyLimit = (
  appId: AppId,
  monthlyLimitMinor: number,
): Promise<Result<MonthlyLimitResponse, ClientError>> =>
  // `apiFetch` directly, since `apiPost` (client.ts:100-105) hardcodes POST.
  apiFetch(`/v1/apps/${appId}/monthly-limit`, MonthlyLimitResponse, {
    method: "PATCH",
    body: JSON.stringify({ monthlyLimitMinor }),
  });
